import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiClient } from '@/api/client';
import { API_HOME_TASKS, API_THIRD_PARTY_EVENTS } from '@/api/endpoints';
import {
  HOME_TASKS_QUERY_KEY,
  THIRD_PARTY_EVENTS_QUERY_KEY,
} from '@/api/queryKeys';
import { buildHomeworkGroups } from '@/components/HomeworksModal/buildHomeworkGroups';
import { useHasFamilySubscription } from '@/hooks/useHasFamilySubscription';
import type {
  HomeTask,
  ThirdPartyEvent,
  ThirdPartyEventResponse,
} from '@/types/diary';
import { unwrapAxiosResponse } from '@/utils/axios';

const toThirdPartyEvent = ({
  classJournalName,
  isDeleted,
  ...event
}: ThirdPartyEventResponse): ThirdPartyEvent => ({
  ...event,
  journalName: classJournalName.disciplineName,
  isDeleted: isDeleted ?? false,
});

export const useHomeworks = () => {
  const hasSubscription = useHasFamilySubscription();

  const { data: homeTasks, isLoading: areHomeTasksLoading } = useQuery({
    queryKey: HOME_TASKS_QUERY_KEY,
    queryFn: () =>
      apiClient.get<HomeTask[]>(API_HOME_TASKS).then(unwrapAxiosResponse),
    enabled: hasSubscription,
    staleTime: Infinity,
  });

  const { data: thirdPartyEvents, isLoading: areEventsLoading } = useQuery({
    queryKey: THIRD_PARTY_EVENTS_QUERY_KEY,
    queryFn: () =>
      apiClient
        .get<ThirdPartyEventResponse[]>(API_THIRD_PARTY_EVENTS)
        .then(unwrapAxiosResponse)
        .then((events) => events.map(toThirdPartyEvent)),
    enabled: hasSubscription,
    staleTime: Infinity,
  });

  const groups = useMemo(
    () =>
      buildHomeworkGroups({
        homeTasks: homeTasks ?? [],
        thirdPartyEvents: thirdPartyEvents ?? [],
      }),
    [homeTasks, thirdPartyEvents],
  );

  return { groups, isLoading: areHomeTasksLoading || areEventsLoading };
};
